---
title: "Terms of Service"
effective: "2026-07-22"
updated: "2026-09-12"
---

These Terms of Service ("**Terms**") are a binding agreement between the adult
accepting them and {publicEnv.siteLegalEntity} ("**Company**", "**we**",
"**us**", or "**our**"). They govern use of {publicEnv.siteTitle} at
`anecdote.fyi`, including its private photo-prompt workflow, kid story studio,
family access, and exports (together, the "**Services**").

By creating a workspace, accepting an invitation, authorizing a child's use,
purchasing access, or otherwise using the Services, you agree to these Terms and
our [Privacy Policy](/privacy). An adult who permits a child to use the Services
agrees to these Terms for that use and remains responsible for the child's
participation. If you do not agree, do not use the Services.

## 1 Adults, children, and accounts

### 1.1 Adult authority and child participation

The Services are an adult-managed family activity. A family owner or guardian
must be an adult with legal capacity to agree to these Terms. A child may use
only a child profile, invited child account, or restricted shared-device session
that an authorized adult creates and manages. A child may not create or own an
unsupervised family workspace.

By creating a child profile or permitting a child to participate, you represent
that you are the child's parent or legal guardian, or otherwise have authority
to make those decisions, accept these Terms for the child's use, and provide any
permission required by law. Do not permit a child to submit personal information
if you lack that authority or have not completed a parental notice,
authorization, or verification step required where you live.

You are responsible for supervising a child's use of the microphone, camera,
photos, prompts, handwriting, exports, and any shared device. The Services are
not a substitute for adult supervision, childcare, education, or an emergency
service.

### 1.2 Registration and account security

You agree to provide accurate account and invitation information and keep it
current. Sign-in may use an email verification link or an approved third-party
identity provider. You are responsible for protecting login links, verification
codes, invitation links, sessions, provider credentials, and guardian exit
codes.

Use a trusted device and end a restricted child session before giving guardian
controls to a child. Promptly contact us if you suspect unauthorized access. We
are not responsible for loss caused by your failure to protect an account or
shared device, except where applicable law provides otherwise.

### 1.3 Family roles and ownership

The first verified adult family member is normally the workspace **owner**. The
owner can manage guardians, transfer ownership, export all family data, and
permanently delete the workspace. Other **guardians** can select and send photo
prompts, manage child profiles and invitations, review responses, recover
removed records, download PDFs, and create exports. A **child** receives only
the active profile, assigned photo-prompt queue, and entries associated with
that child.

You may invite only people you are authorized to include in the family
workspace. Invitations expire and may be revoked. The owner and guardians are
responsible for family-membership and permission decisions. We generally follow
the roles configured in the Services and do not resolve family, custody,
inheritance, or ownership disputes. We may restrict access while responding to a
valid legal request or a credible safety concern.

Removing a family member revokes access but does not automatically delete
already-authored memories or review history. A removed child profile can be
restored by a guardian. The owner should export or address content before
removing access if the family expects a different outcome.

## 2 The Services

### 2.1 Family creativity purpose

The Services help a guardian send a private family photo and optional question
to a child, help that child tell and write a response, and preserve an approved
photo story as a digital or printable family keepsake. The Services are not a
general photo organizer, official school yearbook, archival repository,
professional publishing service, educational assessment, child-development
service, or source of legal or safety advice.

You remain responsible for deciding which photos and prompts are appropriate,
reviewing a child's story and handwriting, checking an export, and deciding
whether and with whom to print or share it.

### 2.2 Local-assisted features

Speech transcription, grammar checking, photo preparation, and handwriting
recognition are automated aids that can be incomplete or wrong. A browser,
device, camera, microphone, model download, or network limitation can prevent a
feature from working. Typed transcript and manual review remain the appropriate
fallbacks.

Do not rely on transcription, grammar, optical recognition, photo dating, or an
export as an exact or permanent record. Review every memory before approving,
printing, downloading, or sharing it.

### 2.3 Photo prompts, review, and output

Guardians choose each photo, optional prompt, child assignment, and review
decision. Approving a response permits it to appear in the applicable family
reader or PDF. The complete family archive also includes drafts, unapproved
pages, and retained history. A completed story requires a captured handwritten
page. Speech, grammar suggestions, and handwriting recognition are optional.
Returning a response lets a guardian request a clearer photograph of the same
paper or a new handwritten page and leave a short note. Rewrites and revisits
are saved as separate pages; retakes preserve the writing date. Earlier approved
handwriting remains available while a new page is underway. The ordinary review
flow does not silently rewrite a child's submitted words.

Print layouts and PDFs can vary across browsers, devices, printers, paper,
fonts, and color settings. You are responsible for checking paper size, layout,
cropping, spelling, image quality, and printer settings before producing or
ordering copies. We do not guarantee a particular physical print result.

### 2.4 Changes and availability

We may correct, revise, add, limit, or remove features and may impose reasonable
storage, file, family-member, photo, version, or request limits. Pilot, beta, or
preview features may change or end and may be less reliable than generally
available features.

Maintenance, security incidents, provider outages, browser changes, legal
requirements, or other events may interrupt access. We may modify or discontinue
all or part of the Services. If discontinuation materially affects paid access,
we will provide the notice or remedy required by law and may offer an additional
remedy at our discretion.

## 3 Plans, trials, and payment

The Services may offer free access, a trial, a one-time purchase, or a paid
plan. The price, duration, renewal status, included features, limits, and other
terms displayed in the offer and hosted checkout when you purchase form part of
these Terms. A checkout-specific term controls over a conflicting general
description here for that purchase.

### 3.1 Checkout and renewal

A payment processor collects checkout and payment details on pages it controls.
You agree to its terms presented during checkout. A purchase is complete only
after the processor confirms successful payment. A pending, abandoned, canceled,
reversed, or failed transaction does not grant paid access.

A plan renews automatically only if the offer and checkout clearly say so. If
you choose an automatically renewing plan, you authorize recurring charges until
cancellation. The account or billing page will provide available controls, and
any legally required renewal notice or cancellation right remains unaffected.

### 3.2 Trials, cancellation, and refunds

A trial may have limits and may end or convert as disclosed when it begins. We
may change or stop a future trial offer, but will honor mandatory rights and any
specific term already accepted for an active trial.

You may cancel a renewing plan using the controls provided or by contacting
{publicEnv.siteSupportEmail}. Unless the offer or applicable law says otherwise,
cancellation stops future renewal and does not refund time already provided.
Reading saved handwriting, comparing pages, downloading PDFs, and exporting the
family archive remain available after ordinary subscription cancellation. New
writing sessions and review actions require an active plan. An adult can turn
off optional reminder emails in Profile at any time.

Deleting a family workspace is permanent and is not a substitute for confirming
the billing result if a cancellation error is shown.

Except where required by law or expressly stated in an offer, paid access is
non-refundable after it is provided. Contact us if a charge was unauthorized,
duplicated, incorrect, or did not provide the access described. Nothing in these
Terms limits a mandatory cooling-off, withdrawal, conformity, refund,
cancellation, or consumer right where you live.

## 4 Family content

"**Family Content**" means selected private photos, legacy links, filenames,
capture dates, prompts, profiles, assignments, drafts, transcripts, child-edited
stories, handwriting, review history, PDF text, feedback tied to a workspace,
and other material a family adds to the Services.

### 4.1 Ownership and limited operating license

As between you and the Company, the family or applicable rights holder retains
ownership of Family Content. You grant the Company a non-exclusive, worldwide,
royalty-free license to host, store, reproduce, transmit, format, normalize,
display, and otherwise process Family Content only as reasonably necessary to:

1. Provide, secure, troubleshoot, and support the Services.
2. Apply family roles, photo assignments, review decisions, recovery, and
   deletion.
3. Create the previews, ZIP archives, JSON files, PDFs, and print layouts an
   authorized family member requests.
4. Comply with law and protect a person's rights or safety.

This license lasts while we need the Family Content for those purposes and ends
when the content is deleted from active systems, subject to limited backups,
legal retention, and rights that by their nature survive. It does not give us
the right to sell Family Content, use it for behavioral advertising, or train a
machine-learning model on it.

### 4.2 Your permissions and responsibilities

You represent that you have the rights, notices, permissions, and consents
needed to add Family Content and permit us to process it under these Terms. This
includes appropriate authority for participating children and permission to use
photos, writing, names, and information about other people.

Do not add content that violates privacy, publicity, copyright, trademark,
contract, child-protection, or other rights. A private family setting does not
eliminate those obligations. If a guardian chooses a legacy public photo link,
that external file may remain accessible outside our private image controls; the
guardian is responsible for the link and its hosting permissions.

You are responsible for the Family Content, family-role choices, approvals,
exports, copies, and any sharing outside the Services. We do not endorse Family
Content or routinely review private workspaces.

### 4.3 Dropbox Chooser, private copies, and exports

A guardian may use Dropbox's provider-hosted Chooser under Dropbox's terms and
privacy policy. Anecdote FYI does not receive a Dropbox access token, connect to
an account, request API scopes, or browse folders. You are responsible for the
Dropbox session within the provider's own interface and for selecting only
photos you are authorized to use.

For each explicit selection, Dropbox sends a temporary direct file link to the
browser. The browser downloads that selected file, normalizes it, removes source
metadata, and uploads a separate private copy to Anecdote FYI. We do not retain
the temporary link, Dropbox account identifier, provider file identifier, path,
or folder metadata. Changing or deleting the Dropbox original does not change an
existing Anecdote FYI copy, and deleting our copy does not delete the Dropbox
original.

A downloaded ZIP or JSON export, PDF, image, screenshot, or printout is also a
separate copy. You are responsible for storing, sharing, and deleting copies
outside the Services.

### 4.4 Recovery, export, and deletion

Photo deletion is recoverable by default until a guardian uses the separate
permanent-purge action. Trashed content continues to consume storage and may
retain assignments and child responses. A permanent purge removes the photo and
its responses and cannot be undone.

Authorized guardians can download the family data export, and authorized family
members can download applicable story PDFs. The family owner can permanently
delete the entire workspace. Workspace deletion removes family content and
account access from active application systems, cannot be undone, and may leave
limited billing, accounting, security, legal, or backup records as described in
the Privacy Policy. Export first if you want a copy.

The Services are not your only backup. Keep an appropriate independent copy of
content you cannot replace.

## 5 Acceptable use and child safety

You agree not to use the Services to:

1. Break any law or violate another person's intellectual-property, privacy,
   publicity, contractual, or other rights.
2. Create, possess, upload, request, or distribute child sexual abuse material
   or content that sexually exploits, grooms, traffics, threatens, or endangers
   a child.
3. Harass, abuse, defame, discriminate against, impersonate, deceive, or expose
   private information about another person.
4. Upload a photo, recording, handwriting sample, or other content without the
   authority and permission needed to use it.
5. Share an account, invitation, verification code, private image route,
   guardian exit code, or export with an unauthorized person.
6. Circumvent family roles, access controls, file limits, or security measures;
   probe for vulnerabilities without written permission; or gain unauthorized
   access to an account, system, or data.
7. Introduce malware or interfere with the security, integrity, availability, or
   normal operation of the Services.
8. Scrape or use automated requests in a way that burdens the Services, extracts
   private family data, or evades a restriction.
9. Use the Services or Family Content to identify, profile, monitor, or make
   consequential decisions about a child without lawful authority.

We may investigate suspected misuse and take proportionate action, including
limiting requests, removing or disabling content, preserving relevant records,
suspending access, reporting apparent exploitation, or contacting appropriate
authorities. If a person may be in immediate danger, contact local emergency
services; do not rely on us to monitor a private workspace.

## 6 Our intellectual property

The Services, including their software, local-assisted workflow, interfaces,
visual design, text, and branding, are owned by the Company or its licensors and
are protected by intellectual-property laws.

Subject to these Terms, we grant you a limited, revocable, non-exclusive,
non-transferable license to access and use the Services for your family's
personal, noncommercial storytelling and keepsake activity. This license does
not permit you to resell access, copy or distribute substantial portions of the
Services, remove rights notices, or use our branding without permission.

Third-party software, model files, fonts, and other material remain subject to
their applicable licenses. If you voluntarily send product ideas, corrections,
or other feedback outside Family Content, you grant us a worldwide, perpetual,
irrevocable, royalty-free right to use that feedback without restriction or
compensation. We will handle personal information in feedback under the Privacy
Policy.

## 7 Third-party services

The Services may interact with providers for optional identity, the Dropbox
Chooser, model-file delivery, email, hosting, payment, printing, documentation,
or other functions. Their services are governed by their own terms and policies.

We do not control third-party services and are not responsible for their
availability, security, accuracy, content, or conduct. A link or optional
integration does not necessarily mean we endorse the provider. Your dealings
with a third party, including a printer selected outside the Services, are
between you and that third party.

## 8 Suspension and termination

You may stop using the Services at any time. We may limit, suspend, or terminate
an account or workspace if you materially or repeatedly violate these Terms,
create a child-safety, security, legal, or payment risk, fail to pay an amount
due, or misuse the Services. When reasonable, we will give an adult owner notice
and an opportunity to cure, but we may act immediately to protect a child,
another person, the Company, or the Services.

Upon termination, the license to use the Services ends. Depending on the reason
and applicable law, we may allow the owner a reasonable opportunity to export
Family Content before deletion, but we do not promise an export when immediate
restriction is needed for safety, security, law, or to protect another person's
rights.

Provisions that by their nature should survive will remain in effect, including
payment obligations, content permissions needed for wind-down or legal
retention, intellectual-property provisions, warranty disclaimers, liability
limitations, indemnification, and dispute terms.

## 9 Disclaimers

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND
“AS AVAILABLE.” THE COMPANY AND ITS LICENSORS DISCLAIM ALL EXPRESS, IMPLIED, AND
STATUTORY WARRANTIES, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AND UNINTERRUPTED OR
ERROR-FREE OPERATION.**

We do not guarantee that local-assisted output, photo dates, transcripts,
handwriting recognition, layouts, PDFs, or print results will be accurate or
complete; that Family Content will never be lost; or that the Services will work
with every browser, device, microphone, camera, file, provider, or printer.

Some jurisdictions do not allow certain disclaimers, so some of the above may
not apply to you. You retain any warranty or consumer protection that cannot
lawfully be excluded.

## 10 Limitation of liability

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY AND ITS OWNERS,
AFFILIATES, EMPLOYEES, CONTRACTORS, LICENSORS, AND SERVICE PROVIDERS WILL NOT BE
LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL
DAMAGES, OR FOR LOST DATA, MEMORIES, PROFITS, REVENUE, GOODWILL, OR BUSINESS
INTERRUPTION, ARISING FROM OR RELATING TO THE SERVICES OR THESE TERMS.**

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THEIR TOTAL AGGREGATE LIABILITY FOR
ALL CLAIMS ARISING FROM OR RELATING TO THE SERVICES OR THESE TERMS WILL NOT
EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US FOR THE SERVICES DURING THE 12
MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM OR (B) US$100.**

These limitations apply regardless of the legal theory and even if a remedy
fails of its essential purpose. They do not limit liability that cannot lawfully
be limited, including liability excluded from limitation by applicable
child-protection, consumer, privacy, or other law.

## 11 Indemnification

To the extent permitted by law, you agree to defend, indemnify, and hold
harmless the Company and its owners, affiliates, employees, contractors, and
licensors from third-party claims and reasonable costs arising from Family
Content you add or authorize, your unlawful use of the Services, your material
violation of these Terms, or your infringement of another person's rights.

This obligation does not apply to a child, does not require indemnification for
the Company's own unlawful conduct, negligence, or breach of these Terms, and
does not limit a protection that cannot lawfully be waived.

## 12 Governing law and disputes

Washington law governs these Terms, without regard to conflict-of-law rules.
Before filing a claim, you and the Company agree to make a good-faith effort to
resolve it informally. Contact us at {publicEnv.siteSupportEmail} with a brief
description of the issue and the relief requested.

If informal resolution fails, the state and federal courts with jurisdiction in
King County, Washington will have exclusive jurisdiction, and each party
consents to venue there. You may bring an eligible individual claim in small
claims court. Nothing in this section deprives you of protections or forums that
the law where you live makes mandatory.

## 13 Changes to these Terms

We may update these Terms to reflect changes in the Services, our practices, or
applicable law. The date at the top identifies the current version. If a change
materially reduces an existing user's rights, we will provide reasonable advance
notice through the Services or by email when required.

Updated Terms apply prospectively when they take effect. If a change requires
new parental authorization or consent for a child's information, we will seek it
where required before applying that change to the child. If you do not agree to
an update, stop using the Services and export or delete Family Content as
appropriate, subject to mandatory rights and provisions that survive.

## 14 General terms

These Terms and the policies incorporated into them are the entire agreement
between you and the Company concerning the Services and replace earlier
agreements about the same subject, except for an offer term we expressly agreed
to preserve. If a provision is unenforceable, it will be modified only as much
as necessary, and the remaining provisions will continue in effect.

Our failure to enforce a provision is not a waiver. You may not assign these
Terms or transfer workspace ownership outside the controls we provide without
our written consent. We may assign these Terms as part of a merger, acquisition,
reorganization, or sale of assets, provided mandatory rights remain protected.
Headings are for convenience only.

## 15 Contact

The Services are provided by {publicEnv.siteLegalEntity}.

For support, safety concerns, legal notices, billing questions, or account
issues, email [support@anecdote.fyi](mailto:support@anecdote.fyi).
